/** Informações de erro
 * @typedef {Object} ErrorInfo
 * @prop {string} msg         - Mensagem do erro
 * @prop {string} type        - Tipo do erro
 * @prop {string} stacktrace  - Stacktrace do erro 
 */

/** Classe mediadora para chamadas de serviço de assinatura
 * @param {SignerSession} signer  - Script control para utilização da assinatura digital
 * @class
 */
function ViewServices(signer) {
    this.certificateList = null;
    this.signer = signer;
};

/** Realiza validação de versão, retornando promise resolved quando não tem problemas
 * @param {Object} [options] Opções para esta operação
 * @returns {Promise} Se não for rejected é porque as versões estão válidas
 * @instance
 */
ViewServices.prototype.whenReady = function (options) {
    const core = QuickSoft.Signer.SignerCore.getInstance();

    if (!core.get_useWebExtension()) return Promise.resolve();

    //verifica se é para fazer bypass do cache de versão
    const bypassVersionCache = (options && "bypassVersionCache" in options) ? !!options["bypassVersionCache"] : this._mustBypassVersionCache();

    //converte chamada de `queryExtensionVersion` em uma promise e joga o resultado para rotina core de check de versão
    return new Promise((resolve) => core.queryExtensionVersion(resolve, bypassVersionCache))
        .then(r => QuickSoft.Signer.checkWebExtRuntimeVersions(r, QuickSoft.Signer.VersionBindings));
};

ViewServices.prototype._mustBypassVersionCache = function() {
    //se não tem API de session storage, é um problema
    if (!window.localStorage) {
        console.error("Não foi possível buscar API window.localStorage");
        return false;
    }

    //tenta remover da session storage a chave que indica que tem que fazer bypass do cache de versão
    var bypassVersionCache = window.localStorage.getItem("QCERT_WEBEXT_BYPASS_VERSION_CACHE");
    if (bypassVersionCache) window.localStorage.removeItem("QCERT_WEBEXT_BYPASS_VERSION_CACHE");

    //converte para booleano e retorna
    return !!bypassVersionCache;
};

ViewServices.prototype._ensureJavaSession = function() 
{
    return new Promise((resolve, reject) => {
        //se já tiver sessão criada, resolve promise aqui mesmo
        if (this.signer.has_activeSession())
            return resolve();
        //cria nova sessão
        this.signer.newJavaSession(resolve);
    });
};

ViewServices.prototype.hasActiveSession = function() 
{
    return this.signer.has_activeSession();
};

ViewServices.prototype._listCerts = function() 
{
    return new Promise((resolve, reject) => 
    {
        this.signer.listCertificates(coreResponse => {
            if (coreResponse.error) reject(coreResponse.error);
            else resolve(coreResponse);
        });
    });
};

/** Lista certificados encontrados na máquina do cliente
 * @param {boolean} reloadCertificateCache  - define se deve limpar cache antes da consulta
 * @returns {Promise} Retorna promise com {@link ListCertificateResult}
 * @instance
 */
ViewServices.prototype.listCertificates = function (reloadCertificateCache) 
{
    let p = this.whenReady()
                .then(this._ensureJavaSession.bind(this));

    if (reloadCertificateCache) {
        p = p.then(() => this.signer.rebuildCertificateCache());
    }

    return p.then(this._listCerts.bind(this));
};

/** Cancela processo de assinatura atual
 * @instance
 */
ViewServices.prototype.cancelCurrentProcess = function() {
    this.signer.cancelSignature();
};

/** Inicia processo de assinatura
 * @param {certificate} certificate  - Certifica a ser utilizado para a assinatura
 * @param {Object} settings          - Configurações do processo de assinatura
 * @param {function} settings.onProgressChanged - Handler para alteração no progresso de assinaturas do lote
 * @instance
 */
ViewServices.prototype.startSignProcess = function (certificate, settings) 
{
    //contexto da promise
    let closureContext = 
    {
        service: this,
        signer: this.signer,
        promisePair: {
            resolve: null,
            reject: null
        },
        onProgressChanged : settings.onProgressChanged,
        onProcessFinished : null
    };

    //handler de finalização do processo de assinatura
    closureContext.onProcessFinished = function(lastError) {
        //desvincula eventos no signer
        if (closureContext.onProgressChanged) closureContext.signer.remove_progressChanged(closureContext.onProgressChanged);
        closureContext.signer.remove_signProcessFinished(closureContext.onProcessFinished);

        //se teve erro, rejeita a promise ou faz resolve nela
        if (lastError) { closureContext.promisePair.reject(lastError); }
        else { closureContext.promisePair.resolve(); }
    };

    //cria promise para o processo de assinatura
    return new Promise((resolve, reject) => 
    {
        //joga no contexto os métodos de controle da promise
        closureContext.promisePair.resolve = resolve;
        closureContext.promisePair.reject  = reject;

        //vincula eventos no signer
        if (closureContext.onProgressChanged) closureContext.signer.add_progressChanged(closureContext.onProgressChanged);
        closureContext.signer.add_signProcessFinished(closureContext.onProcessFinished);

        //inicia um processo de assinatura
        closureContext.service._ensureJavaSession()
            .then(function () {
                closureContext.signer.startSignProcess(certificate);
            })
            .catch(function(err) {
                closureContext.promisePair.reject(err);
            });
    });
};

module.exports = ViewServices;


// WEBPACK FOOTER //
// ./C:/Projetos_Git/qframework_client/src/gui_js/lib/viewservices.js